import api from "@/api/v1-jwt/config/api";
import {
  useQuery,
  QueryFunctionContext,
  UseQueryOptions,
} from "@tanstack/react-query";
import { preSignedUrlSchema, PreSignedUrl, DownloadFileType } from "../types";
import { invoiceQueryKeys } from "../query-keys";
import { createErrorHandler } from "@/utils/errorHandler";

type BulkUploadsDownloadQueryKey = ReturnType<
  typeof invoiceQueryKeys.bulkUploads.download
>;

const fetchBulkInvoiceDownloadUrl = async ({
  queryKey,
}: QueryFunctionContext<BulkUploadsDownloadQueryKey>): Promise<PreSignedUrl> => {
  const [, , , userId, type, uploadId] = queryKey;
  const url = `/platform/invoices/${userId}/bulk/download`;
  const params = { type, ...(uploadId && { upload_id: uploadId }) };
  const response = await api.get(url, { params });
  return preSignedUrlSchema.parse(response.data);
};

export const bulkUploadsDownload = {
  createOptions: <TSelected = PreSignedUrl>(
    userId: string,
    type: DownloadFileType,
    uploadId?: string,
    options?: Omit<
      UseQueryOptions<
        PreSignedUrl,
        Error,
        TSelected,
        BulkUploadsDownloadQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const defaultOptions: UseQueryOptions<
      PreSignedUrl,
      Error,
      TSelected,
      BulkUploadsDownloadQueryKey
    > = {
      queryKey: invoiceQueryKeys.bulkUploads.download(userId, type, uploadId),
      queryFn: fetchBulkInvoiceDownloadUrl,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: createErrorHandler(),
    };
    return { ...defaultOptions, ...options };
  },
  useQuery: <TSelected = PreSignedUrl>(
    userId: string,
    type: DownloadFileType,
    uploadId?: string,
    options?: Omit<
      UseQueryOptions<
        PreSignedUrl,
        Error,
        TSelected,
        BulkUploadsDownloadQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const queryOptions = bulkUploadsDownload.createOptions<TSelected>(
      userId,
      type,
      uploadId,
      options
    );
    return useQuery<
      PreSignedUrl,
      Error,
      TSelected,
      BulkUploadsDownloadQueryKey
    >({
      ...queryOptions,
      enabled: options?.enabled ?? (!!userId && !!type),
    });
  },
};
